import React, { useState } from "react";

import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import CheckIcon from "@mui/icons-material/Check";

import { useUserAuth } from "../contexts/authContext";

const FeedbackFormDialog = ({ setFeedbackButtonClicked, setInfoDialogOpen }) => {
    const [feedbackType, setFeedbackType] = useState("FEEDBACK");
    const [feedbackDescription, setFeedbackDescription] = useState("");
    const [canContact, setCanContact] = useState(true);

    const [feedbackErrorText, setFeedbackErrorText] = useState("");
    const [feedbackRequestPending, setFeedbackRequestPending] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    const { user } = useUserAuth();

    const handleSubmitFeedbackForm = async () => {
        try {
            if (!feedbackDescription.trim()) {
                setFeedbackErrorText("Please provide a description before submitting.");
                return;
            } else if (feedbackErrorText) {
                setFeedbackErrorText("");
            }
            setFeedbackRequestPending(true);

            const body = {
                feedback_type: feedbackType,
                feedback_description: feedbackDescription,
                can_contact: canContact,
                created_user_id: user.id
            };
            const requestOptions = {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            };
            const response = await fetch("api/feedback/insert", requestOptions);
            if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
            setFeedbackSubmitted(true);
        } catch (err) {
            console.error(err);
            setFeedbackErrorText("Something went wrong. Please try again later.");
        } finally {
            setFeedbackRequestPending(false);
        }
    };
    const handleGoBackButtonClick = () => {
        setFeedbackButtonClicked(false);
    };
    const handleCloseDialog = () => {
        setInfoDialogOpen(false);
    };
    return feedbackSubmitted === false ? (
        <>
            <DialogTitle>{"Submit Feedback?"}</DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <DialogContentText>
                    {"Please select the type of feedback you would like to give, followed by a description of the desired behavior."}
                </DialogContentText>

                <FormControl disabled={feedbackRequestPending}>
                    <FormLabel id={`type-label`}>Type</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby={`type-label`}
                        name="row-radio-buttons-group"
                        value={feedbackType}
                        onChange={(e) => setFeedbackType(e.target.value)}
                    >
                        <FormControlLabel value="FEEDBACK" control={<Radio size="small" />} label="Feedback" />
                        <FormControlLabel value="FEATURE" control={<Radio size="small" />} label="New Feature" />
                        <FormControlLabel value="BUG" control={<Radio size="small" />} label="Report Issue" />
                    </RadioGroup>
                </FormControl>

                <TextField
                    fullWidth
                    label="Description"
                    multiline
                    minRows={2}
                    value={feedbackDescription}
                    onChange={(e) => setFeedbackDescription(e.target.value)}
                    variant="outlined"
                    disabled={feedbackRequestPending}
                    error={feedbackErrorText.length > 0 ? true : false}
                    helperText={feedbackErrorText}
                    placeholder={
                        feedbackType === "FEEDBACK"
                            ? "Share your thoughts, suggestions, or experience."
                            : feedbackType === "FEATURE"
                              ? "What problem are you trying to solve, and what would you like to see added?"
                              : "Tell us what went wrong, what was confusing, or how we could improve your experience."
                    }
                    InputLabelProps={{ shrink: true }}
                    autoFocus
                />
                <FormControlLabel
                    control={<Checkbox size="small" checked={canContact} onChange={(e) => setCanContact(e.target.checked)} disabled={feedbackRequestPending} />}
                    label="Contact me about this feedback"
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCloseDialog} sx={{ mr: "auto" }}>
                    {"Close"}
                </Button>
                <Button onClick={handleGoBackButtonClick}>{"Back"}</Button>
                <Button loading={feedbackRequestPending} onClick={handleSubmitFeedbackForm}>
                    {"Submit"}
                </Button>
            </DialogActions>
        </>
    ) : (
        <>
            <DialogTitle />

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", maxWidth: "75%", margin: "auto" }}>
                <CheckIcon fontSize="large" color="success" />

                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body1" gutterBottom>
                        Thank you for your feedback!
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {`We have received your submission${canContact ? ` and will follow up at ${user.email} if we have any questions.` : "."}`}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleGoBackButtonClick}>{"Back"}</Button>
                <Button onClick={handleCloseDialog}>{"Close"}</Button>
            </DialogActions>
        </>
    );
};

export default FeedbackFormDialog;
